'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';
import { Card, CardContent } from '@/components/ui/card';
import {
  IconBuildingSkyscraper,
  IconMapPin,
  IconFileCheck,
  IconUsers,
} from '@tabler/icons-react';
import { ProjectRecord } from '@/lib/projects/project-types';
import { ROUTES } from '@/lib/routes';
import {
  getSectorCounts,
  getCountryCountsTop,
  getCfpRate,
  getTopOrganizations,
} from '@/lib/dashboard/dashboard-utils';

interface InsightCardsProps {
  projects: ProjectRecord[];
}

export const InsightCards = React.memo(function InsightCards({ projects }: InsightCardsProps) {
  const insights = useMemo(() => {
    const sectors = getSectorCounts(projects);
    const countries = getCountryCountsTop(projects, 1);
    const cfp = getCfpRate(projects);
    const orgs = getTopOrganizations(projects, 1);

    return [
      {
        icon: IconBuildingSkyscraper,
        title: 'Top Sector',
        value: sectors[0]?.name ?? '–',
        detail: sectors[0] ? `${sectors[0].value} projects` : 'No data',
      },
      {
        icon: IconMapPin,
        title: 'Top Country',
        value: countries[0]?.name ?? '–',
        detail: countries[0] ? `${countries[0].value} projects` : 'No data',
      },
      {
        icon: IconFileCheck,
        title: 'CFP Availability',
        value: `${cfp.percent}%`,
        detail: `${cfp.withCfp} of ${projects.length} projects have a CFP link`,
      },
      {
        icon: IconUsers,
        title: 'Most Active Organisation',
        value: orgs[0]?.name ?? '–',
        detail: orgs[0] ? `${orgs[0].value} projects` : 'No data',
      },
    ];
  }, [projects]);

  return (
    <Card>
      <CardContent className="pt-0">
        <h3 className="text-base font-medium mb-4">Key Insights</h3>
        <div className="space-y-3">
          {insights.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                className="flex items-start gap-3 rounded-md border bg-muted/20 px-3 py-2.5"
              >
                <Icon className="size-5 text-muted-foreground mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted-foreground">{item.title}</p>
                  <p className="text-sm font-semibold truncate">{item.value}</p>
                  <p className="text-xs text-muted-foreground">{item.detail}</p>
                </div>
              </div>
            );
          })}
        </div>
        <Link
          href={ROUTES.projects}
          className="inline-block text-xs text-primary hover:underline mt-3"
        >
          View in Projects →
        </Link>
      </CardContent>
    </Card>
  );
});
